import React, { useState, createContext, useMemo, useEffect, useLayoutEffect, useContext } from 'react';
import { countryConstants, englishVariant } from './CountryConstants';
import { getCountryByUrl } from '../../../helpers';
import useIsApple from '../../_ReactHooks/useIsApple';
import { ContractContext } from '../ContractContext';

const defaultLangByCountry = {
    US: 'en',
    AUS: 'en',
    UK: 'en',
    DE: 'de',
    ES: 'es',
    FR: 'fr',
    IT: 'it',
    VN: 'vn',
    DA: 'da',
    FI: 'fi',
    SG: 'sg',
    AT: 'at',
    NZ: 'en',
    DK: 'da',
    NL: 'nl',
    NO: 'no',
    SE: 'sv',
    CL: 'cl',
    CH: 'fr',
    IE: 'en',
    JP: 'ja',
    IN: 'en',
    MX: 'mx'
};

const LanguageContext = createContext(null);

const LanguageProvider = ({ children }) => {
    const isApple = useIsApple();
    const { setIsChileApple } = useContext(ContractContext);
    const [loading, setLoading] = useState(true);
    const isCostcoCoverageDefaultLang =
        window.location.href.toLowerCase().includes('aigdeviceprotect')
        || window.location.pathname.toLowerCase().includes('coveragedocscostco');
    const [locale, setLocale] = useState(
        isCostcoCoverageDefaultLang
            ? {
                  country: 'CH',
                  language: 'fr'
              }
            : {
                  country: 'US',
                  language: 'en'
              }
    );
    const [languageBeforeLogin, setLanguageBeforeLogin] = useState('');
    const { country, language } = locale;

    useLayoutEffect(() => {
        if (isApple) {
            getAppleCountryByUrl();
        } else {
            setLoading(false);
        }
    }, [isApple]);

    const getCountryLanguage = async (country) => {
        const res = await fetch('api/session/GetPostedAppleCaseModel');
        if (res.ok && res.status === 200) {
            const data = await res.json();
            return getLanguageByPostedCode(data?.languageCode) || defaultLangByCountry[country];
        } else {
            return defaultLangByCountry[country];
        }
    };

    const getAppleCountryByUrl = async () => {
        const { countryCode } = await getCountryByUrl();
        const country = countryCode?.toUpperCase() || 'US';

        if (isCostcoCoverageDefaultLang) {
            setLocale({
                country: 'FR',
                language: 'fr'
            });

            setLoading(false);
            return;
        }

        const lang = await getCountryLanguage(country);
        //Set true if the country is Chile for Southbrige branding to be used
        country === 'CL' && setIsChileApple(true);

        setLocale({
            country,
            language: lang
        });

        setLoading(false);
    };

    const getLanguageByPostedCode = (langCode) => {
        const lang = langCode?.toLowerCase();
        const languageFromCode = lang?.substring(0, 2);
        const countryFromLangCode = lang?.substring(3);
        switch (countryFromLangCode) {
            case 'au':
            case 'ie':
            case 'nz':
            case 'uk':
            case 'us':
            case 'de':
            case 'es':
            case 'fr':
            case 'it':
            case 'vn':
            case 'da':
            case 'fi':
            case 'dk':
            case 'nl':
            case 'no':
            case 'se':
            case 'jp':
            case 'in':
                return languageFromCode;
            case 'at':
                return languageFromCode === 'de' ? 'at' : languageFromCode;
            case 'cl':
                return languageFromCode === 'es' ? 'cl' : languageFromCode;
            case 'mx':
                return languageFromCode === 'es' ? 'mx' : languageFromCode;
            case 'ch':
                //DE-CH needs to route to SG, other CH languages can use the language from the langCode
                if (languageFromCode === 'de') {
                    return 'sg';
                }
                if (languageFromCode === 'en' || languageFromCode === 'fr' || languageFromCode === 'it') {
                    return languageFromCode;
                }
            case null:
            case undefined:
                return null;
            default:
                return 'en';
        }
    };

    const getEnglishVariantType = (countryOption) => {
        switch (countryOption) {
            case 'us':
                return 'american';
            case 'mx':
                return 'mexican';
            case 'jp':
            case 'in':
                return 'asian';
            default:
                return 'british';
        }
    };

    const englishVars = englishVariant[getEnglishVariantType(country?.toLowerCase())];
    const countryVars = countryConstants[country];

    if (loading) return null;

    return (
        <LanguageContext.Provider
            value={{
                country,
                language,
                englishVars,
                countryVars,
                languageBeforeLogin,
                updateCountry: async (countryCode, languageCode) => {
                    if (locale.country !== countryCode?.toUpperCase() || languageCode) {
                        const countryCodeApplied = countryCode?.toUpperCase() || 'US';
                        const defaultLanguage = !languageCode
                            ? await getCountryLanguage(countryCodeApplied)
                            : getLanguageByPostedCode(languageCode);
                        setLocale({
                            country: countryCodeApplied,
                            language: defaultLanguage
                        });
                    }
                },
                updateLanguage: (lang) => {
                    setLocale((prevLocale) => {
                        return { ...prevLocale, language: lang };
                    });
                },
                updateLanguageBeforeLogin: (lang) => {
                    setLanguageBeforeLogin(lang);
                }
            }}
        >
            {children}
        </LanguageContext.Provider>
    );
};

export { LanguageProvider, LanguageContext };
